// RecommendationOptimizer.tsx
// Recommendation-driven optimization interface replacing manual sliders
// Uses RAG Rubric scoring and Excel-based Big 6 calculations

import React, { useState, useEffect, useMemo, useRef } from 'react';
import apiConfig from '../../../config/apiConfig';
import { authHeaders } from '../../../services/authHeaders';
import { impactCalculator } from '../../../services/recommendationImpactCalculator';
import RecommendationGroundingPanel from './RecommendationGroundingPanel';

// Types for recommendation system
interface RecommendationWithImpact {
  id: string;
  title: string;
  actionSteps: string[];
  
  // RAG Rubric scores
  impact: number;        // 1-5 scale
  effort: number;        // 1-5 scale
  rosi: number;          // Return on Security Investment
  timeline: string;      // "<1mo", "1-3mo", "3-6mo", "6+mo"
  cost: string;          // "$", "$$", "$$$", "$$$$"
  category: 'quick_win' | 'structural_fix' | 'strategic_lever';
  
  // Which 360 questions this recommendation affects
  affects360: {
    questionId: number;
    questionText?: string;
    scoreImprovement: number;  // How much the 1-5 score improves
  }[];
  
  // Estimated Big 6 category impacts
  estimatedImpact: {
    category: string;
    currentScore: number;
    projectedScore: number;
    improvement: number;
  }[];

  // PRISM cross-product enrichment (optional — populated when session is
  // linked to a PRISM project with deployed_tools/deployed_controls)
  prism_status?: 'addressed' | 'upgrade' | 'confirmed_gap' | null;
  prism_match?: string | null;
  prism_action_override?: string | null;
  prism_detail?: string | null;
  prism_grounding?: import('./RecommendationGroundingPanel').PrismGroundingData | null;
}

interface RecommendationOptimizerProps {
  assessmentId: string;
  asIsScores: { [key: string]: number }; // 🎯 NEW: Immutable baseline scores
  toBeScores: { [key: string]: number }; // 🎯 NEW: Projected/modified scores  
  onToBeScoresUpdate: (newToBeScores: { [key: string]: number }) => void; // 🎯 NEW: Only update To-Be
  isSimulating: boolean;
  onSimulationToggle: () => void;
}

const RecommendationOptimizer: React.FC<RecommendationOptimizerProps> = ({
  assessmentId,
  asIsScores, // 🎯 NEW: Immutable baseline (never changes)
  toBeScores, // 🎯 NEW: Projected scores (what gets modified)
  onToBeScoresUpdate, // 🎯 NEW: Only updates To-Be scores
  isSimulating,
  onSimulationToggle
}) => {
  const [recommendations, setRecommendations] = useState<RecommendationWithImpact[]>([]);
  const [checkedRecommendations, setCheckedRecommendations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['quick_win']));

  const initializeCalculator = React.useCallback(async () => {
    try {
      console.log('🎯 Initializing calculator with asIsScores:', asIsScores);
      // Pass the real assessment scores to the calculator
      await impactCalculator.initialize(assessmentId, asIsScores);
      console.log('✅ Impact calculator initialized with real assessment scores');
    } catch (error) {
      console.error('⚠️ Impact calculator initialization failed:', error);
    }
  }, [assessmentId, asIsScores]);

  const loadRecommendations = React.useCallback(async () => {
    try {
      setLoading(true);
      
      const apiBaseUrl = apiConfig.getBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/recommendations/${assessmentId}/optimization`, { headers: authHeaders() });
      if (response.ok) {
        const responseData = await response.json();
        console.log('📊 Loaded recommendations from API:', responseData);
        // Try multiple possible response structures
        const recommendations = responseData.recommendations || responseData.data?.recommendations || [];
        setRecommendations(recommendations);
        console.log('✅ Set recommendations count:', recommendations.length);
      } else {
        console.warn('⚠️ Recommendations API returned', response.status);
        setRecommendations([]);
        setLoadError('Recommendations unavailable — run assessment first');
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
      setRecommendations([]);
      setLoadError('Could not load recommendations');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  // Initialize impact calculator and load recommendations
  useEffect(() => {
    initializeCalculator();
    loadRecommendations();
  }, [initializeCalculator, loadRecommendations]);

  // Impact cache: stores computed impacts per recommendation to avoid mutating rec objects
  const impactCacheRef = useRef<{ [recId: string]: any[] }>({});

  const handleRecommendationToggle = (recId: string) => {
    const isCurrentlyChecked = checkedRecommendations.has(recId);
    const newChecked = new Set(checkedRecommendations);

    const recommendation = recommendations.find(rec => rec.id === recId);
    if (!recommendation) return;
    
    // Apply the delta directly to current To-Be scores
    if (toBeScores) {
      const updatedToBeScores = { ...toBeScores };
      
      // Get or compute impacts for this recommendation (cached to avoid re-mutation)
      const getImpacts = (): any[] => {
        if (recommendation.estimatedImpact && recommendation.estimatedImpact.length > 0) {
          return recommendation.estimatedImpact;
        }
        // Check cache
        if (impactCacheRef.current[recId]) {
          return impactCacheRef.current[recId];
        }
        // Derive impacts from recommendation title keywords
        const title = (recommendation.title || '').toLowerCase();
        let impacts: any[];

        if (title.match(/mfa|multi-factor|authentication/)) {
          impacts = [{ category: 'Governance', improvement: 3 }, { category: 'People', improvement: 2 }];
        } else if (title.match(/vendor|third.party|supplier/)) {
          impacts = [{ category: 'Third Party', improvement: 4 }, { category: 'Governance', improvement: 1 }];
        } else if (title.match(/siem|soc|monitoring/)) {
          impacts = [{ category: 'Technical Debt', improvement: 5 }, { category: 'Governance', improvement: 2 }];
        } else if (title.match(/training|awareness|education/)) {
          impacts = [{ category: 'People', improvement: 4 }, { category: 'Culture', improvement: 3 }];
        } else if (title.match(/policy|procedure|governance/)) {
          impacts = [{ category: 'Governance', improvement: 4 }, { category: 'Culture', improvement: 1 }];
        } else if (title.match(/budget|investment|funding/)) {
          impacts = [{ category: 'Budget', improvement: 3 }, { category: 'Technical Debt', improvement: 2 }];
        } else if (title.match(/zero.trust|network|architecture/)) {
          impacts = [{ category: 'Technical Debt', improvement: 6 }, { category: 'Governance', improvement: 3 }];
        } else if (title.match(/backup|recovery|disaster/)) {
          impacts = [{ category: 'Technical Debt', improvement: 4 }, { category: 'Governance', improvement: 2 }];
        } else if (title.match(/compliance|audit|framework/)) {
          impacts = [{ category: 'Governance', improvement: 5 }, { category: 'Culture', improvement: 2 }];
        } else if (title.match(/endpoint|antivirus|edr/)) {
          impacts = [{ category: 'Technical Debt', improvement: 3 }, { category: 'Governance', improvement: 1 }];
        } else {
          impacts = [{ category: 'Governance', improvement: 2 }];
        }
        impactCacheRef.current[recId] = impacts;
        return impacts;
      };

      const impacts = getImpacts();
      impacts.forEach(impact => {
        // 🎯 FIXED: Comprehensive category name mapping
        let category = impact.category.toLowerCase().replace(/\s+/g, '_');
        
        // Map display names to score object keys (camelCase format)
        const categoryMappings: { [key: string]: string } = {
          'governance': 'governance',
          'people': 'people', 
          'culture': 'culture',
          'budget': 'budget',
          'technical_debt': 'technicalDebt',  // 🎯 FIXED: Use camelCase
          'third_party': 'thirdParty',        // 🎯 FIXED: Use camelCase
          // Handle variations
          'technicaldebt': 'technicalDebt',
          'thirdparty': 'thirdParty'
        };
        
        // Apply mapping if exists, otherwise use the transformed category
        category = categoryMappings[category] || category;
        
        if (updatedToBeScores[category] !== undefined) {
          if (isCurrentlyChecked) {
            updatedToBeScores[category] = Math.max(0, updatedToBeScores[category] - impact.improvement);
          } else {
            updatedToBeScores[category] = Math.max(0, updatedToBeScores[category] + impact.improvement);
          }
        }
      });
      
      onToBeScoresUpdate(updatedToBeScores);
    }
    
    if (isCurrentlyChecked) {
      newChecked.delete(recId);
    } else {
      newChecked.add(recId);
    }
    setCheckedRecommendations(newChecked);
    
    // Auto-enter simulation mode when first recommendation is selected
    if (newChecked.size > 0 && !isSimulating) {
      onSimulationToggle();
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Group recommendations by category
  const groupedRecommendations = useMemo(() => {
    const groups: { [key: string]: RecommendationWithImpact[] } = {
      quick_win: [],
      structural_fix: [],
      strategic_lever: []
    };
    
    recommendations.forEach(rec => {
      // Ensure category exists, default to structural_fix if undefined
      const category = rec.category && groups[rec.category] !== undefined ? rec.category : 'structural_fix';
      groups[category].push(rec);
    });
    
    return groups;
  }, [recommendations]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading recommendations...</div>
      </div>
    );
  }

  if (loadError && recommendations.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', backgroundColor: 'rgba(255, 165, 0, 0.08)', borderRadius: '8px', border: '1px solid rgba(255, 165, 0, 0.25)' }}>
        <div style={{ fontSize: '14px', color: 'var(--ra-warning)', fontWeight: 500 }}>{loadError}</div>
        <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginTop: '8px' }}>
          Complete the assessment to generate personalized recommendations.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'var(--ra-glass-bg)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'var(--ra-text-primary)' }}>
            ✅ Recommendation-Driven Optimization
          </h3>
          <button
            onClick={() => {
              if (isSimulating) {
                // Reset all simulation state without reloading the page
                setCheckedRecommendations(new Set());
                impactCacheRef.current = {};
                if (asIsScores) {
                  onToBeScoresUpdate({ ...asIsScores });
                }
                onSimulationToggle();
              } else {
                onSimulationToggle();
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: isSimulating ? '#dc3545' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: isSimulating ? 'bold' : 'normal'
            }}
          >
            {isSimulating ? '🔄 Master Reset' : '📊 View Current State'}
          </button>
        </div>
        
        {(checkedRecommendations.size > 0 || isSimulating) && (
          <div style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: isSimulating ? 'rgba(255, 165, 0, 0.08)' : 'rgba(0, 217, 255, 0.08)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ra-text-primary)',
            border: isSimulating ? '1px solid rgba(255, 165, 0, 0.25)' : '1px solid rgba(0, 217, 255, 0.15)'
          }}>
            {checkedRecommendations.size > 0 ? (
              <>📈 {checkedRecommendations.size} recommendation{checkedRecommendations.size > 1 ? 's' : ''} selected</>
            ) : (
              <>🎮 No recommendations selected</>
            )}
            {isSimulating && (
              <div style={{ marginTop: '5px', fontWeight: 'bold', color: 'var(--ra-warning)' }}>
                🔒 Simulation Mode Active - Click "Master Reset" to return to real data
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendation Categories */}
      {Object.entries(groupedRecommendations).map(([category, recs]) => (
        <RecommendationCategory
          key={category}
          category={category}
          recommendations={recs}
          checkedRecommendations={checkedRecommendations}
          isExpanded={expandedCategories.has(category)}
          onToggleExpand={() => toggleCategory(category)}
          onToggleRecommendation={handleRecommendationToggle}
        />
      ))}
    </div>
  );
};

// Category component
const RecommendationCategory: React.FC<{
  category: string;
  recommendations: RecommendationWithImpact[];
  checkedRecommendations: Set<string>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleRecommendation: (id: string) => void;
}> = ({
  category,
  recommendations,
  checkedRecommendations,
  isExpanded,
  onToggleExpand,
  onToggleRecommendation
}) => {
  const getCategoryInfo = (cat: string) => {
    switch (cat) {
      case 'quick_win':
        return { 
          title: '🎯 Quick Wins', 
          color: '#28a745',
          description: 'High impact, low effort (< 3 months)'
        };
      case 'structural_fix':
        return { 
          title: '🔧 Structural Fixes', 
          color: '#fd7e14',
          description: 'Moderate effort (3-6 months)'
        };
      case 'strategic_lever':
        return { 
          title: '🚀 Strategic Levers', 
          color: '#6f42c1',
          description: 'Long-term initiatives (6+ months)'
        };
      default:
        return { title: category, color: '#6c757d', description: '' };
    }
  };

  const info = getCategoryInfo(category);
  const checkedCount = recommendations.filter(r => checkedRecommendations.has(r.id)).length;

  return (
    <div style={{ marginBottom: '15px' }}>
      <div
        onClick={onToggleExpand}
        style={{
          padding: '12px',
          backgroundColor: info.color + '20',
          borderLeft: `4px solid ${info.color}`,
          borderRadius: '6px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <span style={{ fontWeight: 'bold', color: info.color }}>
            {info.title}
          </span>
          <span style={{ marginLeft: '10px', fontSize: '12px', color: 'var(--ra-text-secondary)' }}>
            {info.description}
          </span>
          {checkedCount > 0 && (
            <span style={{ 
              marginLeft: '10px', 
              padding: '2px 8px', 
              backgroundColor: info.color,
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px'
            }}>
              {checkedCount} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: 'var(--ra-text-secondary)' }}>
            {recommendations.length} recommendations
          </span>
          <span style={{ fontSize: '18px' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: '10px', marginLeft: '20px' }}>
          {recommendations.map(rec => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              isChecked={checkedRecommendations.has(rec.id)}
              onToggle={() => onToggleRecommendation(rec.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual recommendation card
const RecommendationCard: React.FC<{
  recommendation: RecommendationWithImpact;
  isChecked: boolean;
  onToggle: () => void;
}> = ({ recommendation, isChecked, onToggle }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{
      marginBottom: '10px',
      padding: '12px',
      backgroundColor: isChecked ? 'rgba(0, 217, 255, 0.08)' : 'var(--ra-glass-bg)',
      border: isChecked ? '2px solid var(--ra-cyber-aqua)' : '1px solid var(--ra-glass-border)',
      borderRadius: '8px',
      backdropFilter: 'blur(12px)',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onToggle}
          aria-label={`Select recommendation: ${recommendation.title}`}
          style={{ marginTop: '2px', cursor: 'pointer' }}
        />
        
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: isChecked ? 'bold' : 'normal', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ra-text-primary)' }}>
            {recommendation.title}
            {recommendation.prism_status === 'addressed' && (
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(0, 255, 136, 0.12)', color: 'var(--ra-success)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ✓ PRISM: Addressed
              </span>
            )}
            {recommendation.prism_status === 'upgrade' && (
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(255, 165, 0, 0.12)', color: 'var(--ra-warning)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ↑ PRISM: Upgrade Needed
              </span>
            )}
            {recommendation.prism_status === 'confirmed_gap' && (
              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'rgba(255, 68, 68, 0.12)', color: 'var(--ra-danger)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ✗ PRISM: Gap Confirmed
              </span>
            )}
          </div>

          {/* PRISM-grounded panel — coverage %, matched tools, named gaps,
              before/after action-step diff. Renders only when engine
              attached prism_grounding (linked PRISM project + matched rec) */}
          {recommendation.prism_grounding && (
            <RecommendationGroundingPanel grounding={recommendation.prism_grounding} recTitle={recommendation.title} />
          )}

          {/* Impact metrics */}
          <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: 'var(--ra-text-secondary)' }}>
            <span>Impact: {'⭐'.repeat(recommendation.impact || 1)}</span>
            <span>Effort: {'💪'.repeat(recommendation.effort || 1)}</span>
            <span>ROSI: {(recommendation.rosi || 0).toFixed(1)}</span>
            <span>{recommendation.timeline || 'TBD'}</span>
            <span>{recommendation.cost || '$'}</span>
          </div>
          
          {/* Estimated improvements */}
          {isChecked && recommendation.estimatedImpact.length > 0 && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <strong>Projected Optimization:</strong>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                {recommendation.estimatedImpact.map(impact => (
                  <span key={impact.category}>
                    {impact.category}: {impact.improvement > 0 ? '+' : ''}{impact.improvement} points
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Toggle details */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDetails(!showDetails);
            }}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              backgroundColor: 'transparent',
              color: 'var(--ra-cyber-aqua)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {showDetails ? '▼ Hide Details' : '▶ Show Action Steps'}
          </button>
          
          {/* Action steps */}
          {showDetails && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: 'var(--ra-deep-blue)',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <strong>Action Steps:</strong>
              <ol style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {(() => {
                  // Handle actionSteps - ensure it's properly parsed as an array
                  let actionSteps = recommendation.actionSteps || [];
                  
                  console.log('🔍 RecommendationOptimizer actionSteps:', actionSteps, 'Type:', typeof actionSteps, 'IsArray:', Array.isArray(actionSteps));
                  
                  // If actionSteps is a string representation of an array, parse it
                  if (typeof actionSteps === 'string') {
                    try {
                      // Remove leading numbers and spaces if present (e.g., "1 ['item1']" -> "['item1']")
                      let cleanedString = actionSteps.replace(/^\d+\s*/, '');
                      actionSteps = JSON.parse(cleanedString);
                    } catch (e) {
                      console.warn('Failed to parse actionSteps string:', actionSteps);
                      actionSteps = [actionSteps];
                    }
                  }
                  
                  // Handle the case where actionSteps is an array of malformed strings that need to be joined and parsed
                  if (Array.isArray(actionSteps) && actionSteps.length > 0) {
                    const firstItem = actionSteps[0];
                    if (typeof firstItem === 'string' && (firstItem.includes('[\'') || firstItem.includes('["'))) {
                      try {
                        // Join all array items back together to reconstruct the original string
                        const reconstructedString = actionSteps.join('');
                        console.log('🔧 RecommendationOptimizer reconstructed:', reconstructedString);
                        
                        // Clean up the string and parse as JSON
                        let cleanedString = reconstructedString.replace(/^\d+\s*/, ''); // Remove leading numbers
                        const parsed = JSON.parse(cleanedString);
                        if (Array.isArray(parsed)) {
                          console.log('✅ RecommendationOptimizer successfully parsed:', parsed);
                          actionSteps = parsed;
                        }
                      } catch (e) {
                        console.warn('❌ RecommendationOptimizer failed to reconstruct:', e.message, actionSteps);
                      }
                    }
                  }
                  
                  // Ensure it's an array
                  if (!Array.isArray(actionSteps)) {
                    actionSteps = [];
                  }
                  
                  console.log('🎯 RecommendationOptimizer final actionSteps:', actionSteps);
                  
                  return actionSteps.map((step, idx) => (
                    <li key={idx}>{step}</li>
                  ));
                })()}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Mock data for development
const getMockRecommendations = (): RecommendationWithImpact[] => [
  {
    id: 'rec_001',
    title: 'Implement Multi-Factor Authentication (MFA) enterprise-wide',
    actionSteps: [
      'Select MFA solution compatible with existing systems',
      'Pilot with IT department first',
      'Roll out to all users within 30 days'
    ],
    impact: 5,
    effort: 2,
    rosi: 2.5,
    timeline: '1-3mo',
    cost: '$$',
    category: 'quick_win',
    affects360: [
      { questionId: 101, scoreImprovement: 1.5 },
      { questionId: 102, scoreImprovement: 1.0 }
    ],
    estimatedImpact: [
      { category: 'Governance', currentScore: 49, projectedScore: 54, improvement: 5 },
      { category: 'People', currentScore: 28, projectedScore: 31, improvement: 3 }
    ]
  },
  {
    id: 'rec_002',
    title: 'Establish formal vendor risk assessment program',
    actionSteps: [
      'Create vendor risk assessment template',
      'Classify vendors by criticality',
      'Conduct assessments quarterly'
    ],
    impact: 4,
    effort: 3,
    rosi: 1.3,
    timeline: '3-6mo',
    cost: '$$$',
    category: 'structural_fix',
    affects360: [
      { questionId: 201, scoreImprovement: 2.0 }
    ],
    estimatedImpact: [
      { category: 'Third Party', currentScore: 37, projectedScore: 45, improvement: 8 }
    ]
  },
  {
    id: 'rec_003',
    title: 'Deploy enterprise SIEM with 24/7 SOC',
    actionSteps: [
      'Evaluate SIEM platforms',
      'Design log collection architecture',
      'Establish SOC operations'
    ],
    impact: 5,
    effort: 5,
    rosi: 1.0,
    timeline: '6+mo',
    cost: '$$$$',
    category: 'strategic_lever',
    affects360: [
      { questionId: 301, scoreImprovement: 2.5 },
      { questionId: 302, scoreImprovement: 2.0 }
    ],
    estimatedImpact: [
      { category: 'Technical Debt', currentScore: 44, projectedScore: 52, improvement: 8 },
      { category: 'Governance', currentScore: 49, projectedScore: 55, improvement: 6 }
    ]
  }
];

export default RecommendationOptimizer;